/**
 * Floating image parallax — rich-text and image-with-text sections.
 *
 * Ported 1:1 from the former inline section scripts: same geometry math, same GSAP tweens,
 * same durations, easings and delays. Only the scheduling changed:
 *
 *  - Scroll work runs at most once per frame (first scroll event of a frame runs synchronously,
 *    exactly like before; any extra events in the same frame collapse into one trailing update).
 *  - Once an image's entrance tween has completed, its scroll tween is a single reusable
 *    gsap.quickTo instead of a brand-new gsap.to per scroll event. quickTo restarts from the
 *    current value with the same duration/ease, so the motion is identical, without dozens of
 *    stacked tweens rendering every tick.
 *  - While the entrance tween is still alive, the original gsap.to path is kept (previous scroll
 *    tween killed) so tween ordering against the entrance tween stays exactly as before.
 *
 * Markup contract, emitted by the sections when a floating image has parallax enabled:
 *   data-floating-parallax="rich-text" | "image-with-text"
 *   data-section-id="{{ section.id }}"
 *   data-floating-breakpoint="both|desktop|mobile"   (image-with-text only)
 */
(function () {
  const SCROLL_TWEEN = { ease: 'power1.out', duration: 0.5 };

  const isDesktop = () => window.innerWidth >= 720;

  const shouldRunParallax = (breakpoint) => {
    const desktop = isDesktop();
    return (
      breakpoint === 'both' ||
      (breakpoint === 'desktop' && desktop) ||
      (breakpoint === 'mobile' && !desktop)
    );
  };

  // Offset between the section's center and the viewport's center (0 when aligned).
  const centerOffsetFor = (sectionRect, viewportHeight) => {
    const sectionCenter = sectionRect.top + sectionRect.height / 2;
    const viewportCenter = viewportHeight / 2;
    return sectionCenter - viewportCenter;
  };

  // Translation for one image from its data attributes (speed 1-10 → 0.05-0.5, 'down' inverts).
  const translationFor = (image, centerOffset) => {
    const direction = image.dataset.floatDirection || 'up';
    const speedSetting = parseInt(image.dataset.floatSpeed || '5', 10);
    const scrollSpeed = (speedSetting / 10) * 0.5;
    let translation = centerOffset * scrollSpeed;
    if (direction === 'down') translation = translation * -1;
    return translation;
  };

  const tweenY = (image, y) => {
    if (!image._parallaxEntranceDone) {
      // Entrance tween may still be running: keep the original per-call tween so timeline
      // ordering (scroll tween vs entrance tween) is unchanged. Older scroll tweens never
      // show through (the newest always renders last), so killing them is invisible.
      if (image._parallaxScrollTween) image._parallaxScrollTween.kill();
      image._parallaxScrollTween = gsap.to(image, {
        y,
        ease: SCROLL_TWEEN.ease,
        duration: SCROLL_TWEEN.duration,
      });
      return;
    }
    if (!image._parallaxQuickTo) {
      if (image._parallaxScrollTween) {
        image._parallaxScrollTween.kill();
        image._parallaxScrollTween = null;
      }
      image._parallaxQuickTo = gsap.quickTo(image, 'y', {
        duration: SCROLL_TWEEN.duration,
        ease: SCROLL_TWEEN.ease,
      });
    }
    image._parallaxQuickTo(y);
  };

  // At most one update per frame. The first scroll event of a frame updates synchronously (same
  // timing as the former handler); further events in that frame collapse into one trailing update.
  const bindScroll = (update) => {
    let updatedThisFrame = false;
    let trailing = false;
    window.addEventListener(
      'scroll',
      () => {
        if (updatedThisFrame) {
          trailing = true;
          return;
        }
        updatedThisFrame = true;
        update();
        requestAnimationFrame(() => {
          updatedThisFrame = false;
          if (trailing) {
            trailing = false;
            update();
          }
        });
      },
      { passive: true }
    );
  };

  const initRichText = (section) => {
    const floatingImages = section.querySelectorAll('.content-block--floating');
    if (floatingImages.length === 0) return;

    const initRect = section.getBoundingClientRect();
    const centerOffset = centerOffsetFor(initRect, window.innerHeight);

    floatingImages.forEach((image, index) => {
      const finalY = translationFor(image, centerOffset);

      gsap.set(image, {
        opacity: 0,
        y: finalY + 50,
      });

      gsap.to(image, {
        opacity: 1,
        y: finalY,
        duration: 1,
        delay: 0.5 + index * 0.25,
        ease: 'expo.inOut',
        onComplete: () => {
          image._parallaxEntranceDone = true;
        },
      });
    });

    bindScroll(() => {
      const centerOffset = centerOffsetFor(section.getBoundingClientRect(), window.innerHeight);
      floatingImages.forEach((image) => {
        tweenY(image, translationFor(image, centerOffset));
      });
    });
  };

  const initImageWithText = (section, breakpoint) => {
    const floatingImage = section.querySelector('.media-with-text__media--floating');
    if (!floatingImage) return;

    if (!shouldRunParallax(breakpoint)) return;

    const initRect = section.getBoundingClientRect();
    const sectionHeight = initRect.height;
    const finalY = translationFor(floatingImage, centerOffsetFor(initRect, window.innerHeight));

    gsap.set(floatingImage, {
      opacity: 0,
      y: sectionHeight * 0.25,
    });

    gsap.to(floatingImage, {
      opacity: 1,
      y: finalY,
      duration: 1,
      delay: 0.5,
      ease: 'expo.inOut',
      onComplete: () => {
        floatingImage._parallaxEntranceDone = true;
      },
    });

    bindScroll(() => {
      const centerOffset = centerOffsetFor(section.getBoundingClientRect(), window.innerHeight);
      tweenY(floatingImage, translationFor(floatingImage, centerOffset));
    });

    window.addEventListener('resize', () => {
      if (!shouldRunParallax(breakpoint)) {
        // Reset to natural position if breakpoint no longer matches
        gsap.set(floatingImage, { y: 0, opacity: 1 });
      }
    });
  };

  const initRoot = (root) => {
    if (root._floatingParallaxInit) return;
    root._floatingParallaxInit = true;

    const section = document.getElementById(`shopify-section-${root.getAttribute('data-section-id')}`);
    if (!section) return;

    const mode = root.getAttribute('data-floating-parallax');
    if (mode === 'rich-text') {
      initRichText(section);
    } else if (mode === 'image-with-text') {
      initImageWithText(section, root.getAttribute('data-floating-breakpoint') || '');
    }
  };

  const initAll = (scope) => {
    if (typeof gsap === 'undefined') return;
    scope.querySelectorAll('[data-floating-parallax]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  // Theme editor: sections are re-rendered in place; re-initialise the new markup.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
